import Vue from 'vue';
import rootLogger from '@/utils/root-logger';
import fetcher from '@/utils/fetcher';
import me from './g2w-session/me';
import video from './g2w-session/video';
import handouts from './g2w-session/handouts';
import questions from './g2w-session/questions';
import polls from './g2w-session/polls';

const logger = rootLogger.create('vuex-modules/g2w-session');

export const SESSION_STATUS = {
  inSession: 'inSession',
  joining: 'joining',
  waitStart: 'waitStart',
  left: 'left',
  ended: 'ended',
  none: 'none',
  error: 'error'
};

/* eslint no-throw-literal: "warn" */
/* eslint no-param-reassign: "warn" */

const mutations = {
  setJoinInfo(state, payload) {
    state.joinInfo = payload.joinInfo;
  },

  setSessionStatus(state, payload) {
    const status = payload.sessionStatus;
    if (!SESSION_STATUS[status]) {
      throw new Error(`invalid sessionStatus ${status}`);
    }
    state.sessionStatus = status;
  },

  setCanRaiseHand(state, payload) {
    state.canRaiseHand = payload.canRaiseHand;
  },

  setHandRaised(state, payload) {
    state.handRaised = payload.handRaised;
  },

  addChatMessage(state, payload) {
    const chatMessage = payload.chatMessage;
    chatMessage.time = chatMessage.time || Date.now();
    state.chatMessages.push(chatMessage);
  },

  setChatMessages(state, payload) {
    state.chatMessages = payload.chatMessages;
  },

  setGlobalPermission(state, payload) {
    const globalPermission = payload.globalPermission;
    Vue.set(state.globalPermissions, globalPermission.name, globalPermission.value);
  },

  setGlobalPermissions(state, payload) {
    state.globalPermissions = payload.globalPermissions;
  },

  setParticipant(state, payload) {
    const participant = payload.participant;

    const index = state.participants.findIndex((p) => {
      if (p.id === participant.id) {
        return true;
      }

      if (participant.audio) {
        if (p.audio && p.audio.pin && p.audio.pin === participant.audio.pin) {
          return true;
        }

        return (participant.audio.voip && p.voipConnectionId === participant.audio.voip)
          || (participant.audio.pstn && p.pstnConnectionId === participant.audio.pstn);
      }

      return false;
    });

    if (index >= 0) {
      const updatedParticipant = Object.assign({}, state.participants[index], participant);
      Vue.set(state.participants, index, updatedParticipant);
    } else {
      state.participants.push(participant);
    }
  },

  setParticipants(state, payload) {
    state.participants = payload.participants;
  },

  setMcsEnded(state, payload) {
    state.mcsEnded = payload.ended;
  }
};

const actions = {
  async getAttendeeJoinInfo({commit, dispatch, rootGetters}, payload) {
    const registrantKey = await dispatch('g2wWebinar/normalizeRegistrantKey', payload, { root: true });
    const operation = 'getAttendeeJoinInfo';
    const {webinarKey} = payload;
    logger.log(operation, {webinarKey, registrantKey});

    const path = `/api/webinars/${webinarKey}/webAttendee/${registrantKey}/joinInfo`;
    const url = rootGetters['g2wEnv/globalUrl'](path);

    const joinInfo = await fetcher.fetchJSON(url, {}, {operation});
    commit('setJoinInfo', { joinInfo });
    return joinInfo;
  },

  async leave({
    commit, state, rootState, dispatch
  }, payload = {}) {
    const sessionStatus = payload.userInitiated ? SESSION_STATUS.left : SESSION_STATUS.ended;

    state.closedBrowser = payload.closedBrowser;

    commit('setSessionStatus', {sessionStatus});

    try {
      if (rootState.mcsComm) {
        await dispatch('mcsComm/leave', {}, {root: true});
      }

      if (rootState.g2wBroadcast) {
        await dispatch('g2wBroadcast/leave', {}, {root: true});
      }

      if (rootState.g2wSimulive) {
        await dispatch('g2wSimulive/leave', {joinInfo: state.joinInfo}, {root: true});
      }

      if (rootState.rtcgw) {
        if (rootState.rtcgw.audio) {
          await dispatch('rtcgw/audio/leave', {}, {root: true});
        }

        if (rootState.rtcgw.video) {
          await dispatch('rtcgw/video/leave', {}, {root: true});
        }
      }
    } catch (error) {
      logger.log('leave error', {error});
    }

    // commit('setJoinInfo', {joinInfo: null});
    // commit('setCanRaiseHand', {raiseHand: false});
    // commit('setHandRaised', {handRaised: false});
    // commit('questions/reset');
    // commit('setChatMessages', {chatMessages: []});
    // commit('setParticipants', {participants: []});
    // commit('setGlobalPermissions', {globalPermissions: {}});

    // dispatch('handouts/reset');

    // commit('g2wWebinar/reset', {}, {root: true});
  },

  async toggleHandRaising({dispatch}, payload) {
    return dispatch('mcsComm/toggleHandRaising', payload, {root: true});
  },

  async sendChatMessage({dispatch}, payload) {
    return dispatch('mcsComm/sendChatMessage', payload, {root: true});
  }
};

const getters = {
  inSession(state) {
    return state.sessionStatus === SESSION_STATUS.inSession;
  },

  chatEnabled(state) {
    return state.globalPermissions.canChat && state.joinInfo.allowChatAndQA;
  },

  questionsEnabled(state) {
    const liveEnabled = state.globalPermissions.allCanAskQuestions
      && state.joinInfo.allowChatAndQA;

    const simuliveEnabled = !!state.questions.settings && state.questions.settings.enableQandA;

    return liveEnabled || simuliveEnabled;
  },

  organizerParticipants(state) {
    return state.participants.filter((participant) => participant.isOrganizer);
  },

  presenterParticipant(state) {
    const presenters = state.participants.filter((participant) => participant.isPresenter);
    return presenters[0];
  },

  allParticipants(state) {
    return state.participants.reduce((map, participant) => {
      map[participant.id] = participant;
      return map;
    }, {});
  },

  onlineParticipants(state) {
    const onlineParticipants = {};
    state.participants.forEach((participant) => {
      if (participant.state === 'Active') {
        onlineParticipants[participant.id] = participant;
      }
    });

    return onlineParticipants;
  },

  isScheduled(state, localGetters, rootState, rootGetters) {
    const { sessionType = '' } = state.joinInfo || {};
    return rootGetters['g2wWebinar/isLive'] || sessionType.toUpperCase() === 'SCHEDULED';
  }
};

const state = () => ({
  joinInfo: null,
  sessionStatus: SESSION_STATUS.none,
  closedBrowser: false,
  canRaiseHand: false,
  handRaised: false,
  chatMessages: [],
  participants: [],
  globalPermissions: {},
  internalActions: {},
  mcsEnded: false
});

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules: {
    me,
    handouts,
    video,
    questions,
    polls
  }
};
